import { Outlet, useNavigate } from 'react-router';

import useAuth from '@/hooks/useAuth';
import { usePlansShouldShowTabs } from '@/hooks/usePlansShouldShowTabs';
import { useResetViewStateOnSectionLeave } from '@/hooks/useResetViewStateOnSectionLeave';

import { useFeaturePermission } from '@/utils/permissions';
import { cn } from '@/utils/tailwind';
import { useRouteMatcher } from '@/utils/url';

import { Header, Tabs } from '@/components/header';

import Constants, { routes } from 'constants/index';

export default function PlansLayout() {
  useResetViewStateOnSectionLeave('/plans');
  const routeMatches = useRouteMatcher();
  const navigate = useNavigate();
  const { data: auth } = useAuth();
  const plansEnabled = useFeaturePermission('plans');
  const managePlansEnabled = useFeaturePermission('plans.manage');
  const quizzesFeatureEnabled = useFeaturePermission('quizzes.create');
  const canManageQuizzesAnywhere =
    auth?.locationsWithRoles.some(
      location => location.permissions.canManageQuizzes,
    ) ?? false;
  const quizzesEnabled = quizzesFeatureEnabled && canManageQuizzesAnywhere;
  const shouldShowTabs = usePlansShouldShowTabs();

  const activeTab = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingMyPlansPath,
  )
    ? routes.plans.plans
    : routeMatches.includes(Constants.ROUTE_PATH_GROUPS.TrainingTeamPath)
      ? routes.plans.managedPlans
      : routeMatches.includes(Constants.ROUTE_PATH_GROUPS.TrainingQuizzesPath)
        ? routes.plans.managedQuizzes
        : undefined;

  return (
    <div className="relative min-h-screen w-full">
      <Header.Root>
        {!window.native.isNative && (
          <Header.Title tx="TrainingPlans.tabPlans" />
        )}
        {shouldShowTabs && (
          <Tabs.Root
            selectedKey={activeTab}
            onSelectionChange={navigate}
            style={{
              top: `calc(var(--web-alert-banner-height, 0px)${window.native.isNative ? '' : ' + var(--spacing-header-height, 0px))'}`,
            }}
          >
            {plansEnabled && (
              <Tabs.Tab id={routes.plans.plans} tx="TrainingPlans.tabMy" />
            )}
            {managePlansEnabled && (
              <Tabs.Tab
                id={routes.plans.managedPlans}
                tx="TrainingPlans.tabManagePlans"
              />
            )}
            {quizzesEnabled && (
              <Tabs.Tab
                id={routes.plans.managedQuizzes}
                tx="TrainingPlans.tabManageQuizzes"
              />
            )}
          </Tabs.Root>
        )}
      </Header.Root>
      <div
        className={cn(
          'max-w-content mx-auto px-4 max-md:mt-[78px]',
          shouldShowTabs
            ? window.native.isNative
              ? 'md:mt-[calc(var(--header-content-spacing-lg)+47px)]'
              : 'md:mt-[calc(var(--header-height)+var(--header-content-spacing-lg)+47px)]'
            : window.native.isNative
              ? 'md:mt-header-content-spacing-lg'
              : 'md:mt-header-height-with-content-spacing-lg',
        )}
      >
        <Outlet />
      </div>
    </div>
  );
}
